import type { VariantProps } from 'cva'
import { motion } from 'motion/react'
import { createContext, use, useId, useRef, useState, useCallback } from 'react'

import { cva } from '~/utils/classnames'

const rootStyle = cva({
  base: ['p-1 isolate', 'flex items-center gap-0.5', 'rounded-[10px]'],
  variants: {
    variant: {
      default: 'bg-under/5 overall:bg-under/8 dark:bg-under/16',
      'over-media': 'bg-under/64 backdrop-blur-md',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

interface SegmentedControlContextType {
  name: string
  variant: VariantProps<typeof rootStyle>['variant']
  value?: string
  onValueChange?: (value: string) => void
}

const SegmentedControlContext =
  createContext<SegmentedControlContextType | null>(null)

const useSegmentedControlContext = () => {
  const context = use(SegmentedControlContext)

  if (context == null) {
    throw new Error(
      'SegmentedControl components must be wrapped in <SegmentedControl />'
    )
  }

  return context
}

interface SegmentedControlRootProps {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  className?: string
  name?: string
  variant?: VariantProps<typeof rootStyle>['variant']
}

const SegmentedControlRoot = ({
  value: valueProp,
  defaultValue,
  onValueChange: onValueChangeProp,
  children,
  className,
  name: providedName,
  variant = 'default',
  ...props
}: SegmentedControlRootProps) => {
  const generatedId = useId()
  const name = providedName ?? generatedId

  const [internalValue, setInternalValue] = useState(defaultValue)

  const value = valueProp ?? internalValue

  const onValueChange = useCallback(
    (newValue: string) => {
      setInternalValue(newValue)
      onValueChangeProp?.(newValue)
    },
    [onValueChangeProp]
  )

  return (
    <SegmentedControlContext
      value={{
        name,
        variant,
        value,
        onValueChange,
      }}
    >
      <div
        role="radiogroup"
        className={rootStyle({ variant, className })}
        {...props}
      >
        {children}
      </div>
    </SegmentedControlContext>
  )
}

const itemStyle = cva({
  base: [
    'group relative',
    'h-7 min-w-7 px-1.5',
    'flex items-center justify-center gap-1.5',
    'text-xs font-medium',
    'rounded-md',
    'cursor-pointer select-none whitespace-nowrap',
    'transition',
    'has-disabled:pointer-events-none has-disabled:opacity-48',
    'has-focus-visible:ring-2 has-focus-visible:ring-accent-element',
  ],
  variants: {
    variant: {
      default: [
        'text-foreground/64 hover:text-foreground has-checked:text-foreground has-active:text-foreground',
      ],
      'over-media':
        'text-highlight/64 hover:text-highlight has-checked:text-highlight has-active:text-highlight',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const indicatorStyle = cva({
  base: 'absolute inset-0 rounded-[inherit] z-[-1]',
  variants: {
    variant: {
      default: 'bg-highlight dark:bg-highlight/20 overall:bg-foreground/12',
      'over-media': 'bg-highlight/20',
    },
  },
})

const SegmentedControlItem = ({
  value,
  children,
  className,
  disabled,
  style,
  checked,
  onChange,
}: Omit<React.ComponentPropsWithRef<'input'>, 'type'> & {
  value?: string
  children: React.ReactNode
}) => {
  const {
    name,
    variant,
    value: rootValue,
    onValueChange,
  } = useSegmentedControlContext()
  const inputRef = useRef<HTMLInputElement>(null)

  // Individual checked prop takes precedence over root value
  const isChecked =
    checked !== undefined ? checked : value !== undefined && rootValue === value

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Call individual onChange first
      onChange?.(e)

      // Then call root onValueChange if value is provided and not prevented
      if (!e.defaultPrevented && value !== undefined && onValueChange) {
        onValueChange(value)
      }
    },
    [onChange, value, onValueChange]
  )

  return (
    <label className={itemStyle({ variant, className })} style={style}>
      {children}
      <input
        ref={inputRef}
        className="peer sr-only"
        type="radio"
        name={name}
        value={value}
        checked={isChecked}
        onChange={handleChange}
        disabled={disabled}
      />
      {isChecked && (
        <motion.span
          layoutId={name}
          layoutDependency={isChecked}
          aria-hidden="true"
          className={indicatorStyle({ variant })}
          transition={{ type: 'spring', duration: 0.2, bounce: 0.2 }}
        />
      )}
    </label>
  )
}

const SegmentedControl = Object.assign(SegmentedControlRoot, {
  Item: SegmentedControlItem,
})

export { SegmentedControl }
